import React, { useState, useRef, useEffect } from 'react';
import { evaluate } from 'mathjs';
import '../../../css/formulaEditor.css';

/**
 * 🧮 FormulaEditor - Editor visual avanzado de fórmulas matemáticas
 * 
 * Props:
 * - show: boolean - Mostrar/ocultar modal
 * - onClose: function - Callback al cerrar
 * - onSave: function - Callback al guardar (recibe la fórmula)
 * - formula: string - Fórmula inicial
 * - testValue: number - Valor de prueba inicial
 * - variables: array - Variables disponibles [{nb_var_idn, vr_var_nom, vr_var_umd}, ...]
 * - colors: object - Colores del tema
 */
const FormulaEditor = ({ show, onClose, onSave, formula = 'x', testValue = 0, variables = [], colors = {} }) => {
  const inputRef = useRef(null);
  
  // Estados
  const [currentFormula, setCurrentFormula] = useState(formula);
  const [testVal, setTestVal] = useState(testValue);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('calculator'); // 'calculator' | 'functions' | 'variables' | 'examples'
  const [cursorPosition, setCursorPosition] = useState(0);
  const [searchVars, setSearchVars] = useState(''); // ✅ NUEVO: Búsqueda de variables

  // Categorías de funciones
  const basicOperators = [
    { label: '+', insert: ' + ', tooltip: 'Suma' },
    { label: '−', insert: ' - ', tooltip: 'Resta' },
    { label: '×', insert: ' * ', tooltip: 'Multiplicación' },
    { label: '÷', insert: ' / ', tooltip: 'División' },
    { label: '^', insert: ' ^ ', tooltip: 'Potencia' },
    { label: '(', insert: '(', tooltip: 'Abre paréntesis' },
    { label: ')', insert: ')', tooltip: 'Cierra paréntesis' },
    { label: '%', insert: ' % ', tooltip: 'Módulo (resto)' },
  ];

  const functionCategories = {
    'Trigonométricas': [
      { label: 'sin()', insert: 'sin(x)' },
      { label: 'cos()', insert: 'cos(x)' },
      { label: 'tan()', insert: 'tan(x)' },
      { label: 'asin()', insert: 'asin(x)' },
      { label: 'acos()', insert: 'acos(x)' },
      { label: 'atan()', insert: 'atan(x)' },
    ],
    'Potencia y Raíz': [
      { label: '√ sqrt()', insert: 'sqrt(x)' },
      { label: 'x²', insert: 'pow(x, 2)' },
      { label: 'x³', insert: 'pow(x, 3)' },
      { label: 'eˣ', insert: 'exp(x)' },
      { label: 'pow(a,b)', insert: 'pow(a, b)' },
    ],
    'Logarítmicas': [
      { label: 'log()', insert: 'log(x)' },
      { label: 'log10()', insert: 'log10(x)' },
      { label: 'ln()', insert: 'log(x)' }, // mathjs usa log para ln
    ],
    'Estadísticas': [
      { label: 'abs()', insert: 'abs(x)' },
      { label: 'min(a,b)', insert: 'min(a, b)' },
      { label: 'max(a,b)', insert: 'max(a, b)' },
      { label: 'round()', insert: 'round(x)' },
      { label: 'floor()', insert: 'floor(x)' },
      { label: 'ceil()', insert: 'ceil(x)' },
    ],
    'Constantes': [
      { label: 'π', insert: 'pi' },
      { label: 'e', insert: 'e' },
    ],
  };

  // Sincronizar cuando cambia la fórmula prop
  useEffect(() => {
    setCurrentFormula(formula);
  }, [formula]);

  // Sincronizar cuando cambia testValue prop
  useEffect(() => {
    setTestVal(testValue);
  }, [testValue]);

  // Calcular resultado en tiempo real
  useEffect(() => {
    if (currentFormula.trim() === '') {
      setResult(null);
      setError(null);
      return;
    }

    try {
      // Reemplazar variables con sus valores
      let evalFormula = currentFormula;
      
      // Reemplazar x con testVal
      evalFormula = evalFormula.replace(/\bx\b/g, `(${testVal})`);
      
      // Reemplazar referencias a variables (var1, var2, etc.)
      variables.forEach((v, index) => {
        const varName = `var${index + 1}`;
        evalFormula = evalFormula.replace(new RegExp(`\\b${varName}\\b`, 'g'), `(${testVal})`);
      });

      const evalResult = evaluate(evalFormula);
      setResult(parseFloat(evalResult).toFixed(4));
      setError(null);
    } catch (err) {
      setResult(null);
      setError(`Error: ${err.message}`);
    }
  }, [currentFormula, testVal, variables]);

  // Insertar función en la posición del cursor
  const insertFunction = (text) => {
    const start = cursorPosition;
    const end = cursorPosition;
    
    const newFormula = currentFormula.substring(0, start) + text + currentFormula.substring(end);
    setCurrentFormula(newFormula);
    
    // Mover cursor después de insertar
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newPos = start + text.length;
        inputRef.current.setSelectionRange(newPos, newPos);
        setCursorPosition(newPos);
      }
    }, 0);
  };

  // Insertar variable
  const insertVariable = (varName) => {
    insertFunction(varName);
  };

  // Actualizar posición del cursor
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentFormula(value);
    setCursorPosition(e.target.selectionStart);
  };

  // Guardar y cerrar
  const handleSave = () => {
    if (currentFormula.trim() === '') {
      setError('La fórmula no puede estar vacía');
      return;
    }
    if (error) {
      setError('La fórmula contiene errores. Corrige antes de guardar.');
      return;
    }
    onSave(currentFormula);
    handleClose();
  };

  // Cerrar sin guardar
  const handleClose = () => {
    setCurrentFormula(formula);
    setTestVal(testValue);
    setError(null);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="formula-editor-modal-overlay" onClick={handleClose}>
      <div 
        className="formula-editor-modal" 
        style={{
          backgroundColor: colors.colorBg || '#ffffff',
          color: colors.colorFont || '#000000',
          borderColor: colors.colorFont || '#000000'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="formula-editor-header">
          <h5 className="formula-editor-title">🧮 Editor de Fórmulas Avanzadas</h5>
          <button 
            className="btn-close formula-editor-close"
            onClick={handleClose}
            style={{ color: colors.colorFont || '#000000' }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="formula-editor-body">
          {/* Sección de entrada */}
          <div className="formula-editor-input-section">
            <label className="formula-editor-label">Fórmula:</label>
            <input
              ref={inputRef}
              type="text"
              className="formula-editor-input"
              value={currentFormula}
              onChange={handleInputChange}
              onMouseUp={(e) => setCursorPosition(e.target.selectionStart)}
              onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
              placeholder="Ej: x * 2 + 10, sin(x) / 2, etc."
              style={{
                backgroundColor: colors.colorBg || '#ffffff',
                color: colors.colorFont || '#000000',
                borderColor: colors.colorFont || '#666666'
              }}
            />
            <small className="formula-editor-hint">
              💡 Usa <code>x</code> para la variable de entrada, <code>var1</code>, <code>var2</code> para otras variables
            </small>
          </div>

          {/* Sección de prueba */}
          <div className="formula-editor-test-section">
            <div className="formula-editor-test-group">
              <label className="formula-editor-label">Valor de Prueba:</label>
              <input
                type="number"
                className="formula-editor-test-input"
                value={testVal}
                onChange={(e) => setTestVal(parseFloat(e.target.value))}
                placeholder="0"
                style={{
                  backgroundColor: colors.colorBg || '#ffffff',
                  color: colors.colorFont || '#000000',
                  borderColor: colors.colorFont || '#666666'
                }}
              />
            </div>

            <div className="formula-editor-result-group">
              <label className="formula-editor-label">Resultado:</label>
              <div 
                className={`formula-editor-result ${error ? 'error' : result !== null ? 'success' : 'empty'}`}
                style={{
                  backgroundColor: error ? '#ffebee' : result !== null ? '#e8f5e9' : colors.colorBg || '#f5f5f5',
                  color: error ? '#c62828' : result !== null ? '#2e7d32' : colors.colorFont || '#666666',
                  borderColor: error ? '#c62828' : result !== null ? '#2e7d32' : colors.colorFont || '#cccccc'
                }}
              >
                {error ? error : result !== null ? result : 'Pendiente...'}
              </div>
            </div>
          </div>

          {/* Tabs de funciones y variables */}
          <div className="formula-editor-tabs">
            <button
              className={`formula-editor-tab ${activeTab === 'calculator' ? 'active' : ''}`}
              onClick={() => setActiveTab('calculator')}
              style={{
                color: activeTab === 'calculator' ? (colors.colorFont || '#000000') : (colors.colorFont || '#999999'),
                borderBottomColor: activeTab === 'calculator' ? (colors.colorFont || '#000000') : 'transparent'
              }}
            >
              🧮 Calculadora
            </button>
            <button
              className={`formula-editor-tab ${activeTab === 'functions' ? 'active' : ''}`}
              onClick={() => setActiveTab('functions')}
              style={{
                color: activeTab === 'functions' ? (colors.colorFont || '#000000') : (colors.colorFont || '#999999'),
                borderBottomColor: activeTab === 'functions' ? (colors.colorFont || '#000000') : 'transparent'
              }}
            >
              ƒ Funciones
            </button>
            <button
              className={`formula-editor-tab ${activeTab === 'variables' ? 'active' : ''}`}
              onClick={() => setActiveTab('variables')}
              style={{
                color: activeTab === 'variables' ? (colors.colorFont || '#000000') : (colors.colorFont || '#999999'),
                borderBottomColor: activeTab === 'variables' ? (colors.colorFont || '#000000') : 'transparent'
              }}
            >
              𝑥 Variables
            </button>
            <button
              className={`formula-editor-tab ${activeTab === 'examples' ? 'active' : ''}`}
              onClick={() => setActiveTab('examples')}
              style={{
                color: activeTab === 'examples' ? (colors.colorFont || '#000000') : (colors.colorFont || '#999999'),
                borderBottomColor: activeTab === 'examples' ? (colors.colorFont || '#000000') : 'transparent'
              }}
            >
              📚 Ejemplos
            </button>
          </div>

          {/* Contenido de tabs */}
          <div className="formula-editor-content">
            {activeTab === 'calculator' && (
              <div className="formula-editor-calculator">
                <div className="formula-editor-operators">
                  {basicOperators.map((op, idx) => (
                    <button
                      key={idx}
                      className="formula-editor-operator-btn"
                      onClick={() => insertFunction(op.insert)}
                      title={op.tooltip}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2',
                        borderColor: colors.colorFont || '#1976d2'
                      }}
                    >
                      {op.label}
                    </button>
                  ))}
                </div>
                
                <div className="formula-editor-quick-numbers">
                  <h6 className="formula-editor-category-title">Números Rápidos</h6>
                  <div className="formula-editor-numbers-grid">
                    {[0, 1, 2, 3, 4, 5, 10, 100].map((num) => (
                      <button
                        key={num}
                        className="formula-editor-number-btn"
                        onClick={() => insertFunction(String(num))}
                        style={{
                          backgroundColor: colors.colorFont ? `${colors.colorFont}20` : '#f0f0f0',
                          color: colors.colorFont || '#333333',
                          borderColor: colors.colorFont || '#cccccc'
                        }}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="formula-editor-quick-vars">
                  <h6 className="formula-editor-category-title">Variables Frecuentes</h6>
                  <div className="formula-editor-var-quick">
                    <button
                      className="formula-editor-var-quick-btn"
                      onClick={() => insertFunction('x')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}15` : '#e8f5e9',
                        color: colors.colorFont || '#2e7d32',
                        borderColor: colors.colorFont || '#4caf50'
                      }}
                    >
                      x (Entrada)
                    </button>
                    {variables.slice(0, 2).map((v, idx) => (
                      <button
                        key={v.nb_var_idn}
                        className="formula-editor-var-quick-btn"
                        onClick={() => insertFunction(`var${idx + 1}`)}
                        style={{
                          backgroundColor: colors.colorFont ? `${colors.colorFont}15` : '#f3e5f5',
                          color: colors.colorFont || '#7b1fa2',
                          borderColor: colors.colorFont || '#9c27b0'
                        }}
                      >
                        var{idx + 1} ({v.vr_var_nom})
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'functions' && (
              <div className="formula-editor-functions">
                {Object.entries(functionCategories).map(([category, functions]) => (
                  <div key={category} className="formula-editor-category">
                    <h6 className="formula-editor-category-title">{category}</h6>
                    <div className="formula-editor-buttons-grid">
                      {functions.map((func, idx) => (
                        <button
                          key={idx}
                          className="formula-editor-function-btn"
                          onClick={() => insertFunction(func.insert)}
                          title={func.insert}
                          style={{
                            backgroundColor: colors.colorFont ? `${colors.colorFont}20` : '#f0f0f0',
                            color: colors.colorFont || '#333333',
                            borderColor: colors.colorFont || '#cccccc'
                          }}
                        >
                          {func.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'variables' && (
              <div className="formula-editor-variables">
                {variables.length > 0 ? (
                  <>
                    {/* ✅ NUEVO: Buscador de variables */}
                    <div className="formula-editor-var-search">
                      <input
                        type="text"
                        placeholder="🔍 Buscar variable..."
                        value={searchVars}
                        onChange={(e) => setSearchVars(e.target.value)}
                        className="formula-editor-search-input"
                        style={{
                          borderColor: colors.colorFont || '#cccccc',
                          color: colors.colorFont || '#333333'
                        }}
                      />
                    </div>

                    {/* Variable de entrada */}
                    <div className="formula-editor-var-group">
                      <button
                        className="formula-editor-var-btn"
                        onClick={() => insertVariable('x')}
                        style={{
                          backgroundColor: colors.colorFont ? `${colors.colorFont}20` : '#f0f0f0',
                          color: colors.colorFont || '#333333',
                          borderColor: colors.colorFont || '#cccccc'
                        }}
                      >
                        <strong>x</strong> - Entrada
                      </button>
                    </div>

                    {/* Variables filtradas */}
                    <div className="formula-editor-var-group">
                      <div className="formula-editor-var-list">
                        {variables
                          .filter(v => 
                            searchVars === '' ||
                            v.vr_var_nom.toLowerCase().includes(searchVars.toLowerCase())
                          )
                          .map((v, originalIdx) => {
                            const idx = variables.indexOf(v);
                            return (
                              <div key={v.nb_var_idn} className="formula-editor-var-item">
                                <button
                                  className="formula-editor-var-btn"
                                  onClick={() => insertVariable(`var${idx + 1}`)}
                                  style={{
                                    backgroundColor: colors.colorFont ? `${colors.colorFont}20` : '#f0f0f0',
                                    color: colors.colorFont || '#333333',
                                    borderColor: colors.colorFont || '#cccccc'
                                  }}
                                >
                                  <strong>var{idx + 1}</strong>
                                </button>
                                <span className="formula-editor-var-name">
                                  {v.vr_var_nom} ({v.vr_var_umd})
                                </span>
                              </div>
                            );
                          })}
                      </div>
                      {searchVars !== '' && variables.filter(v => 
                        v.vr_var_nom.toLowerCase().includes(searchVars.toLowerCase())
                      ).length === 0 && (
                        <p style={{ color: '#999', textAlign: 'center', fontSize: '12px', marginTop: '10px' }}>
                          Sin resultados para "{searchVars}"
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <p style={{ color: colors.colorFont || '#666666', textAlign: 'center', padding: '20px' }}>
                    No hay variables disponibles
                  </p>
                )}
              </div>
            )}

            {activeTab === 'examples' && (
              <div className="formula-editor-examples">
                <div className="formula-editor-examples-grid">
                  {/* Ejemplo 1: Escala lineal */}
                  <div className="formula-editor-example-card">
                    <h6 className="formula-editor-example-title">📊 Escala Lineal</h6>
                    <div className="formula-editor-example-code">x * 2 + 10</div>
                    <p className="formula-editor-example-desc">
                      <strong>Uso:</strong> Multiplicar entrada por 2 y sumarle 10
                    </p>
                    <p className="formula-editor-example-usage">
                      <strong>Ejemplo:</strong> Si x = 5 → Resultado = 20
                    </p>
                    <button
                      className="formula-editor-example-insert"
                      onClick={() => setCurrentFormula('x * 2 + 10')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2'
                      }}
                    >
                      Usar esta fórmula
                    </button>
                  </div>

                  {/* Ejemplo 2: Conversión de temperatura */}
                  <div className="formula-editor-example-card">
                    <h6 className="formula-editor-example-title">🌡️ Conversión Fahrenheit</h6>
                    <div className="formula-editor-example-code">(x * 9/5) + 32</div>
                    <p className="formula-editor-example-desc">
                      <strong>Uso:</strong> Convertir Celsius a Fahrenheit
                    </p>
                    <p className="formula-editor-example-usage">
                      <strong>Ejemplo:</strong> Si x = 0°C → Resultado = 32°F
                    </p>
                    <button
                      className="formula-editor-example-insert"
                      onClick={() => setCurrentFormula('(x * 9/5) + 32')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2'
                      }}
                    >
                      Usar esta fórmula
                    </button>
                  </div>

                  {/* Ejemplo 3: Función cuadrática */}
                  <div className="formula-editor-example-card">
                    <h6 className="formula-editor-example-title">⬆️ Función Cuadrática</h6>
                    <div className="formula-editor-example-code">x ^ 2 - 3*x + 2</div>
                    <p className="formula-editor-example-desc">
                      <strong>Uso:</strong> Aplicar función cuadrática ax² + bx + c
                    </p>
                    <p className="formula-editor-example-usage">
                      <strong>Ejemplo:</strong> Si x = 2 → Resultado = 0
                    </p>
                    <button
                      className="formula-editor-example-insert"
                      onClick={() => setCurrentFormula('x ^ 2 - 3*x + 2')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2'
                      }}
                    >
                      Usar esta fórmula
                    </button>
                  </div>

                  {/* Ejemplo 4: Logaritmo */}
                  <div className="formula-editor-example-card">
                    <h6 className="formula-editor-example-title">📈 Escala Logarítmica</h6>
                    <div className="formula-editor-example-code">log10(x + 1)</div>
                    <p className="formula-editor-example-desc">
                      <strong>Uso:</strong> Aplicar escala logarítmica base 10
                    </p>
                    <p className="formula-editor-example-usage">
                      <strong>Ejemplo:</strong> Si x = 99 → Resultado = 2
                    </p>
                    <button
                      className="formula-editor-example-insert"
                      onClick={() => setCurrentFormula('log10(x + 1)')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2'
                      }}
                    >
                      Usar esta fórmula
                    </button>
                  </div>

                  {/* Ejemplo 5: Valor absoluto con conditionals */}
                  <div className="formula-editor-example-card">
                    <h6 className="formula-editor-example-title">|x| Valor Absoluto</h6>
                    <div className="formula-editor-example-code">abs(x - 100)</div>
                    <p className="formula-editor-example-desc">
                      <strong>Uso:</strong> Distancia desde un punto de referencia
                    </p>
                    <p className="formula-editor-example-usage">
                      <strong>Ejemplo:</strong> Si x = 80 → Resultado = 20
                    </p>
                    <button
                      className="formula-editor-example-insert"
                      onClick={() => setCurrentFormula('abs(x - 100)')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2'
                      }}
                    >
                      Usar esta fórmula
                    </button>
                  </div>

                  {/* Ejemplo 6: Exponencial */}
                  <div className="formula-editor-example-card">
                    <h6 className="formula-editor-example-title">📊 Exponencial</h6>
                    <div className="formula-editor-example-code">exp(x / 10)</div>
                    <p className="formula-editor-example-desc">
                      <strong>Uso:</strong> Aplicar función exponencial
                    </p>
                    <p className="formula-editor-example-usage">
                      <strong>Ejemplo:</strong> Si x = 10 → Resultado ≈ 2.718
                    </p>
                    <button
                      className="formula-editor-example-insert"
                      onClick={() => setCurrentFormula('exp(x / 10)')}
                      style={{
                        backgroundColor: colors.colorFont ? `${colors.colorFont}30` : '#e3f2fd',
                        color: colors.colorFont || '#1976d2'
                      }}
                    >
                      Usar esta fórmula
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="formula-editor-footer">
          <button
            className="formula-editor-btn-cancel"
            onClick={handleClose}
            style={{
              backgroundColor: 'transparent',
              color: colors.colorFont || '#333333',
              borderColor: colors.colorFont || '#cccccc'
            }}
          >
            Cancelar
          </button>
          <button
            className={`formula-editor-btn-save ${error || currentFormula.trim() === '' ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={!!error || currentFormula.trim() === ''}
            style={{
              backgroundColor: (error || currentFormula.trim() === '') ? '#cccccc' : '#4CAF50',
              color: '#ffffff'
            }}
          >
            ✓ Guardar Fórmula
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormulaEditor;
